import { useNavigate } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onMount,
  Show,
} from "solid-js";

import { fetchFirms, type FirmSummary } from "../api";

const MAX_RESULTS_EMPTY = 50;
const MAX_RESULTS_QUERY = 8;

function fmtRevenue(b: number | null): string {
  if (b == null) return "n/a";
  if (b >= 1000) return `$${(b / 1000).toFixed(1)}T`;
  return `$${b.toFixed(0)}B`;
}

export default function SpotlightSearch(props: { onClose: () => void }) {
  const navigate = useNavigate();
  const [firmsRes] = createResource(fetchFirms);
  const [query, setQuery] = createSignal("");
  const [selectedIdx, setSelectedIdx] = createSignal(0);
  let inputRef: HTMLInputElement | undefined;
  let resultsRef: HTMLDivElement | undefined;

  const firms = () => firmsRes()?.items ?? [];

  const results = createMemo<FirmSummary[]>(() => {
    const q = query().trim().toLowerCase();
    const all = firms();
    if (!q) {
      // Default list: order by Fortune 500 rank so it reads #1, #2, #3 ... in
      // sequence with the rank shown on each row (matches the /firms page).
      return [...all]
        .sort((a, b) => {
          const ra = a.f500_rank ?? Number.POSITIVE_INFINITY;
          const rb = b.f500_rank ?? Number.POSITIVE_INFINITY;
          if (ra !== rb) return ra - rb;
          return a.name.localeCompare(b.name);
        })
        .slice(0, MAX_RESULTS_EMPTY);
    }
    return all
      .filter((f) => {
        const hay = `${f.name} ${f.ticker ?? ""} ${f.sector ?? ""} ${f.hq ?? ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, MAX_RESULTS_QUERY);
  });

  const open = (slug: string) => {
    navigate(`/firm/${slug}`);
    props.onClose();
  };

  const handleKey = (e: KeyboardEvent) => {
    const r = results();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(i + 1, Math.max(0, r.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const f = r[selectedIdx()];
      if (f) open(f.slug);
    } else if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
    }
  };

  // Keep selectedIdx within bounds when results change
  createEffect(() => {
    const r = results();
    if (selectedIdx() >= r.length) setSelectedIdx(0);
  });

  // Scroll active result into view as user arrows down
  createEffect(() => {
    const idx = selectedIdx();
    if (!resultsRef) return;
    const el = resultsRef.children[idx] as HTMLElement | undefined;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  });

  onMount(() => {
    setTimeout(() => inputRef?.focus(), 0);
  });

  return (
    <div
      class="overlay spotlight-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search firms"
      onClick={props.onClose}
    >
      <div class="spotlight-card" onClick={(e) => e.stopPropagation()}>
        <div class="spotlight-search">
          <span class="spotlight-icon" aria-hidden>&#9906;</span>
          <input
            ref={inputRef}
            type="search"
            class="spotlight-input"
            placeholder="Search firms by name, ticker, sector, HQ..."
            value={query()}
            onInput={(e) => {
              setQuery(e.currentTarget.value);
              setSelectedIdx(0);
            }}
            onKeyDown={handleKey}
            autocomplete="off"
            spellcheck={false}
          />
          <span class="spotlight-count">
            {results().length} of {firms().length}
          </span>
        </div>
        <div class="spotlight-results" ref={resultsRef}>
          <Show
            when={results().length}
            fallback={<div class="spotlight-empty">no firms match "{query()}"</div>}
          >
            <For each={results()}>
              {(f, i) => (
                <button
                  type="button"
                  class="spotlight-result"
                  classList={{ active: i() === selectedIdx() }}
                  onClick={() => open(f.slug)}
                  onMouseEnter={() => setSelectedIdx(i())}
                >
                  <span class="result-rank">#{f.f500_rank ?? "?"}</span>
                  <span class="result-name">{f.name}</span>
                  <span class="result-ticker">{f.ticker ?? ""}</span>
                  <span class="result-rev">{fmtRevenue(f.revenue_b_usd_fy2025)}</span>
                  <span class="result-sector">{f.sector}</span>
                </button>
              )}
            </For>
          </Show>
        </div>
        <div class="spotlight-foot">
          <span><kbd>&uarr;</kbd><kbd>&darr;</kbd> navigate</span>
          <span><kbd>&crarr;</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
